import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { postDeleteGL, postDeleteGLCategory } from '../../../../api/invoicing';
import { getGLs } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { createColumns } from '../../../../reusable-components/datagrid/custom-filters';
import { GL_SETTINGS } from '../../../../reusable-components/datagrid/invoiceColumns';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import NewGLModal from './NewGlModal';
import GLCustomToolbar from './GLCustomToolbar';


export default function ManualGLTable() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const SELECTED_GLS_STORAGE_KEY = 'selectedInvoiceGLs';
  const [data, setData] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedGLs, setSelectedGLs] = useState(
    () => JSON.parse(localStorage.getItem(SELECTED_GLS_STORAGE_KEY)) || []
  );
  const { data: { invoicingGLs, editableGLFacilities } } = useSelector((state) => state.invoicing);
  const isLoading = useSelector((state) => state.invoicing.isLoading);

  useEffect(() => {
    dispatch(getGLs());
  }, []);

  useEffect(() => {
    if (invoicingGLs !== undefined && invoicingGLs !== null) {
      setData(invoicingGLs);
    }
  }, [invoicingGLs]);

  useEffect(() => {
    const selectableRows = new Set(data.filter((row) => isRowEditable(row)).map((row) => getGLRowId(row)));
    const validSelections = selectedGLs.filter((id) => selectableRows.has(id));

    if (validSelections.length !== selectedGLs.length) {
      updateSelectedGLs(validSelections);
    }
  }, [data, editableGLFacilities]);

  const deleteGL = async (glId, categoryId) => {
    try {
      const res = glId ? await postDeleteGL(glId) : await postDeleteGLCategory(categoryId);
      if (res.data) {
        enqueueSnackbar(res.data.message, { variant: res.data.success ? 'success' : 'error' });
      }
      await dispatch(getGLs());
    } catch {
      enqueueSnackbar('Something went wrong', { variant: 'error' });
    }

  };

  const isRowEditable = (row) => editableGLFacilities?.some((f) => f.value === row.facilityId);
  const getGLRowId = (row) => `${row.subcategoryId}_${row.categoryId}`;

  const updateSelectedGLs = (selection) => {
    setSelectedGLs(selection);
    if (selection.length > 0) {
      localStorage.setItem(
        SELECTED_GLS_STORAGE_KEY,
        JSON.stringify(selection)
      );
    } else {
      localStorage.removeItem(SELECTED_GLS_STORAGE_KEY);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGLs.length === 0) return;
    const selectedSet = new Set(selectedGLs);
    const selectedRows = data.filter((row) =>
      selectedSet.has(getGLRowId(row))
    );
    const successCount = await selectedRows.reduce(
      (promise, row) => promise.then(async (count) => {
        try {
          const res = row.subcategoryId
            ? await postDeleteGL(row.subcategoryId)
            : await postDeleteGLCategory(row.categoryId);

          return res?.data?.success ? count + 1 : count;
        } catch {
          // Keep deleting remaining records and summarize at the end.
          return count;
        }
      }),
      Promise.resolve(0)
    );

    const failureCount = selectedRows.length - successCount;
    if (failureCount === 0) {
      enqueueSnackbar(`Deleted ${successCount} GL item${successCount === 1 ? '' : 's'}.`, { variant: 'success' });
    } else if (successCount === 0) {
      enqueueSnackbar('Could not delete selected GL items.', { variant: 'error' });
    } else {
      enqueueSnackbar(
        `Deleted ${successCount} GL item${successCount === 1 ? '' : 's'}. ${failureCount} failed.`, { variant: 'warning' });
    }

    await dispatch(getGLs());
    updateSelectedGLs([]);
    setIsSelecting(false);
  };

  const customToolbar = useCallback(() => (
    <GLCustomToolbar
      isSelecting={isSelecting}
      setIsSelecting={setIsSelecting}
      selectedGLs={selectedGLs}
      updateSelectedGLs={updateSelectedGLs}
      handleBulkDelete={handleBulkDelete}
    />
  ), [isSelecting, selectedGLs.length]);

  const actionButtons = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    flex: 1,
    renderCell: (params) => (
      isRowEditable(params.row) && <>
        <Box>
          <ConfirmDialog
            icon="delete-outline"
            title="Delete"
            color="error"
            content={params.row.subcategoryId ? "Are you sure you want to delete this GL?"
              : "Are you sure you want to delete this department?"}
            actionButton="Delete"
            action={() => deleteGL(params.row.subcategoryId, params.row.categoryId)}
          /></Box>
        <Box>
          <NewGLModal row={params.row} edit />
        </Box>
      </>
    ),
    id: 'actions',
  };

  const columns = createColumns([...GL_SETTINGS, actionButtons]);

  return (
    <>
      {editableGLFacilities?.length > 0 &&  //Action buttons appearing depend on permitted glFacilities loading
        <Grid item xs={12}>
          <CustomDataGrid
            gridId="invoicing-vendors-manual-gls"
            boxSX={{ height: 'calc(100vh - 280px)' }}
            data={data}
            sort={[{ field: 'invoiceVendorId', sort: 'asc' }]}
            gridColumns={[...columns]}
            getRowId={getGLRowId}
            CustomLeftToolbar={customToolbar}
            checkboxSelection={isSelecting}
            isRowSelectable={(params) => isRowEditable(params.row)}
            rowSelectionModel={selectedGLs}
            onRowSelectionModelChange={(newSelection) => {
              updateSelectedGLs(newSelection || []);
            }}
            onCellClick={(row, event, api) => {
              const selection = window.getSelection().toString();
              const apiObj = api.api;

              if (selection.length > 0 || !isSelecting) return;
              if (!isRowEditable(row.row)) return;

              const isSelected = apiObj.isRowSelected(row.id);
              apiObj.selectRow(row.id, !isSelected);
            }}
            isLoading={isLoading || !editableGLFacilities}
          />
        </Grid>
      }
    </>
  );
}
